import { useState } from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import FeedbackAlert from "../../components/common/FeedbackAlert";
import AssessmentStep from "../../components/perhitungan/assessment/AssessmentStep";
import HasilHeader from "../../components/perhitungan/Hasil/HasilHeader";
import SummaryCard from "../../components/perhitungan/Hasil/SummaryCard";
import MakroChart from "../../components/perhitungan/Hasil/MakroChart";
import FaktorPerhitungan from "../../components/perhitungan/Hasil/FaktorPerhitungan";
import StatusGizi from "../../components/perhitungan/Hasil/StatusGizi";
import HasilAction from "../../components/perhitungan/Hasil/HasilAction";

import { savePerhitungan } from "../../services/perhitungan/simpanPerhitunganApi";
import { updateRiwayat } from "../../services/riwayat/riwayatApi";
import PortalBackground from "../../components/portal/PortalBackground";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import SuccessModal from "../../components/perhitungan/Hasil/SuccessModal";
import { prepareSavePayload } from "./hasilHelpers";

export default function HasilPerhitungan() {

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const {
    data,
    hasil,
  } = location.state || {};

  const hasilPreview = 
    hasil?.data?.perhitungan?.hasil;

  const [feedback, setFeedback] =
    useState(null);

  const [isSaving, setIsSaving] =
    useState(false);

  const [
    showSuccessModal,
    setShowSuccessModal,
  ] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);

  const handleBack = () => {
    const patientObj = {
      id: data?.id_pasien,
      nama: data?.nama,
      rm: data?.noRM,
      umur: data?.umur,
      jk: data?.jenisKelamin === "P" ? "Perempuan" : "Laki-laki"
    };

    navigate("/assessment", {
      state: {
        fromHasil: true,
        restoredData: data,
        patient: patientObj
      }
    });
  };

  const handleExportPDF = () => {
    const originalTitle = document.title;
    const patientName = data?.nama ? String(data.nama).replace(/\s+/g, "_") : "Pasien";
    const noRm = data?.noRM || "";
    document.title = `Hasil_Perhitungan_Gizi_${patientName}${noRm ? `_${noRm}` : ""}`;
    window.print();
    document.title = originalTitle;
  };

  const handleSave = () => {
    setShowConfirm(true);
  };

  const executeSave = async () => {
    setShowConfirm(false);
    setIsSaving(true);
    setFeedback(null);

    try {
      const payload = prepareSavePayload(data, hasil);
      
      let response;
      if (data?.isEditMode && data?.id_perhitungan) {
        response = await updateRiwayat(data.id_perhitungan, payload);
      } else {
        response = await savePerhitungan(payload);
      }

      if (response.status === "success") {
        setFeedback({
          type: "success",
          message: response.message || (data?.isEditMode ? "Riwayat perhitungan berhasil diperbarui" : "Riwayat perhitungan berhasil disimpan"),
        });
        setShowSuccessModal(true);
      } else {
        throw new Error(response.message || "Gagal menyimpan perhitungan");
      }
    } catch (error) {
      setFeedback({
        type: "error",
        message: error.message || "Perhitungan gagal disimpan. Silakan coba kembali.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (

    <div
      className="
        min-h-screen
        bg-[#f8fbff]
        px-4
        py-6
        sm:px-6
        sm:py-8
        relative
        overflow-hidden
      "
    >
      <PortalBackground />

      <div
        className="
          max-w-7xl
          mx-auto
          space-y-6
          print-compact-space
          relative
          z-10
        "
      >

        {/* KOP CETAK HANYA DILAMPIRKAN SAAT EXPORT PDF */}
        <div className="hidden print:block mb-4 border-b-2 border-slate-800 pb-3 text-center">
          <h1 className="text-xl font-bold uppercase tracking-wider text-slate-900">RSUD PRINGSEWU</h1>
          <h2 className="text-sm font-semibold text-slate-700">Laporan Hasil Perhitungan Status & Kebutuhan Gizi Pasien</h2>
          <p className="text-xs text-slate-500 mt-0.5">Sistem Informasi Gizi - RSUD Pringsewu</p>
        </div>

        <div className="no-print">
          <AssessmentStep activeStep={2} />
        </div>

        <FeedbackAlert
          type={feedback?.type}
          message={feedback?.message}
          onClose={() =>
            setFeedback(null)
          }
        />

        <div className="max-w-5xl mx-auto w-full space-y-6 print-compact-space">
          <HasilHeader
            data={{
              ...data,
              diagnosis: data?.isDiagnosisEdited ? (data?.diagnosis || "-") : (hasil?.data?.kode_penyakit || data?.diagnosis || "-"),
              tanggal_masuk: hasil?.data?.tanggal_masuk_rapi || data?.tanggal_masuk || "-",
              tanggal_masuk_rapi: hasil?.data?.tanggal_masuk_rapi || data?.tanggal_masuk_rapi,
              tanggal_perhitungan_rapi: hasil?.data?.tanggal_perhitungan_rapi || data?.tanggal_perhitungan_rapi,
              jam_perhitungan: hasil?.data?.jam_perhitungan || data?.jam_perhitungan,
            }}
          />

          <StatusGizi
            data={{
              ...data,
              bb: data?.bb,
              tb: data?.tb,
              imt_nilai: hasil?.data?.imt_preview?.nilaiIMT,
              imt_status: hasil?.data?.imt_preview?.statusGizi,
            }}
          />
        </div>

        <SummaryCard
          hasil={{
            energi: hasilPreview?.energi_kkal || 0,
            protein: hasilPreview?.protein_gr || 0,
            lemak: hasilPreview?.lemak_gr || 0,
            karbohidrat: hasilPreview?.karbohidrat_gr || 0,
          }}
        />

        {/* Grid row: Combined Nutrition Card and Factors Card side-by-side */}
        <div
          className="
            grid
            grid-cols-1
            xl:grid-cols-2
            gap-6
            print-grid-cols-2
            print-break-before-page
          "
        >
          <MakroChart
            hasil={hasilPreview}
            persen={hasil?.data?.perhitungan?.persen}
            data={data}
          />

          <FaktorPerhitungan
            data={{
              ...data,
              bbi: hasil?.data?.berat_badan_ideal || 0,
              bmr: hasil?.data?.data_simpan?.bmr || 0,
              aktivitasFisikLabel: data?.aktivitasFisik || "",
              faktorAktivitasNilai: hasil?.data?.data_simpan?.faktor_aktivitas_nilai ?? 0,
              faktorStressLabel: data?.faktorStress || "",
              faktorStressNilai: hasil?.data?.data_simpan?.faktor_stres_nilai ?? 0,
              penambahanKaloriNilai: hasil?.data?.data_simpan?.penambahan_kalori ?? 0,
            }}
          />
        </div>

        <HasilAction
          navigate={navigate}
          onSave={handleSave}
          isSaving={isSaving}
          onBack={handleBack}
          onExportPDF={handleExportPDF}
        />

{/* SUCCESS MODAL */}
<SuccessModal
  isOpen={showSuccessModal}
  isEdit={data?.isEditMode}
  patientName={data?.nama}
  noRM={data?.noRM}
  statusGizi={hasil?.data?.imt_preview?.statusGizi || data?.imt_status}
  energi={hasilPreview?.energi_kkal || 0}
  protein={hasilPreview?.protein_gr || 0}
  lemak={hasilPreview?.lemak_gr || 0}
  karbohidrat={hasilPreview?.karbohidrat_gr || 0}
/>
      <ConfirmationModal
        isOpen={showConfirm}
        title={data?.isEditMode ? "Perbarui Hasil Perhitungan" : "Simpan Hasil Perhitungan"}
        message={
          data?.isEditMode
            ? "Apakah Anda yakin ingin memperbarui riwayat perhitungan gizi pasien ini ke database?"
            : "Apakah Anda yakin ingin menyimpan riwayat perhitungan gizi pasien ini ke database?"
        }
        onConfirm={executeSave}
        onCancel={() => setShowConfirm(false)}
        confirmText={data?.isEditMode ? "Perbarui" : "Simpan"}/>
</div>
</div>
);
}